import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  Building2,
  Check,
  ChevronRight,
  ClipboardList,
  FileText,
  Loader2,
} from 'lucide-react';
import { useState } from 'react';
import { AppLayout } from '../../components/layout/app-layout';
import { api } from '../../lib/ky';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';
import { AccountContactStep } from '../permits/components/AccountContactStep';

export const Route = createFileRoute('/study-requests/new')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('sm-study:create')) throw redirect({ to: '/dashboard' });
  },
  component: NewStudyRequestPage,
});

// ── Types ──────────────────────────────────────────────────────────────────────

interface AccountData {
  accountId: string;
  accountName: string;
  contactId: string;
  contactName: string;
}

interface DetailsData {
  studyTypeId: string;
  studyTypeCode: string;
  studyTypeName: string;
  requiredStudy: string;
  serviceCount: string;
  reportSubmissionDate: string;
  reportValidityDate: string;
  crmCode: string;
  tahseelReferenceNo: string;
}

interface StudyType {
  id: string;
  code: string;
  name: string;
  totalFee: string;
}

interface FeeBreakdown {
  studyType: { id: string; name: string; beeahAmount: number; smAmount: number };
  measurementPoints: { measurementPointTypeId: string; name: string; subtotal: number }[];
  grandTotal: number;
}

const defaultAccount: AccountData = {
  accountId: '',
  accountName: '',
  contactId: '',
  contactName: '',
};

const defaultDetails: DetailsData = {
  studyTypeId: '',
  studyTypeCode: '',
  studyTypeName: '',
  requiredStudy: '',
  serviceCount: '',
  reportSubmissionDate: '',
  reportValidityDate: '',
  crmCode: '',
  tahseelReferenceNo: '',
};

// ── Step indicator ─────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Account & Contact', icon: Building2 },
  { id: 2, label: 'Study Details', icon: FileText },
  { id: 3, label: 'Review & Submit', icon: ClipboardList },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((s, i) => {
        const done = s.id < current;
        const active = s.id === current;
        return (
          <div key={s.id} className="flex items-center flex-1 last:flex-none">
            <div className="flex items-center gap-2.5">
              <div
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors',
                  done
                    ? 'bg-success-500 text-white'
                    : active
                      ? 'bg-brand-500 text-white'
                      : 'bg-gray-100 text-gray-400',
                )}
              >
                {done ? <Check size={14} /> : s.id}
              </div>
              <span
                className={cn(
                  'text-xs font-semibold whitespace-nowrap',
                  active ? 'text-brand-600' : done ? 'text-success-600' : 'text-gray-400',
                )}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={cn(
                  'mx-3 h-px flex-1 transition-colors',
                  s.id < current ? 'bg-success-300' : 'bg-gray-200',
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Fee breakdown display ──────────────────────────────────────────────────────

function FeePreview({ studyTypeId }: { studyTypeId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['study-fee-preview', studyTypeId],
    queryFn: () =>
      api.get('study-requests/fee-preview', { searchParams: { studyTypeId } }).json<FeeBreakdown>(),
    enabled: !!studyTypeId,
    staleTime: 60_000,
  });

  if (!studyTypeId) return null;
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-400">
        <Loader2 size={14} className="animate-spin" /> Calculating fee…
      </div>
    );
  }
  if (!data) return null;

  const fmt = (n: number) => new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2 }).format(n);

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50 p-4 space-y-3">
      <p className="text-xs font-semibold text-brand-700 uppercase tracking-wide">Fee Preview</p>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Beeah Fee</span>
          <span className="font-semibold">AED {fmt(data.studyType.beeahAmount)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">SM Fee</span>
          <span className="font-semibold">AED {fmt(data.studyType.smAmount)}</span>
        </div>
        {data.measurementPoints.length > 0 &&
          data.measurementPoints.map((mp) => (
            <div key={mp.measurementPointTypeId} className="flex justify-between text-gray-500">
              <span>{mp.name}</span>
              <span>AED {fmt(mp.subtotal)}</span>
            </div>
          ))}
        <div className="border-t border-brand-200 pt-1.5 flex justify-between font-bold text-brand-700">
          <span>Grand Total</span>
          <span>AED {fmt(data.grandTotal)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Form fields helper ─────────────────────────────────────────────────────────

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

function NewStudyRequestPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [account, setAccount] = useState<AccountData>(defaultAccount);
  const [details, setDetails] = useState<DetailsData>(defaultDetails);
  const [detailsErrors, setDetailsErrors] = useState<Partial<Record<keyof DetailsData, string>>>(
    {},
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: studyTypes, isLoading: studyTypesLoading } = useQuery({
    queryKey: ['config', 'study-types'],
    queryFn: () => api.get('config/study-types').json<StudyType[]>(),
    staleTime: 300_000,
  });

  const accountValid = !!(account.accountId && account.contactId);

  function validateDetails(): boolean {
    const errs: Partial<Record<keyof DetailsData, string>> = {};
    if (!details.studyTypeCode) errs.studyTypeCode = 'Study type is required';
    setDetailsErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleDetailsChange(key: keyof DetailsData, value: string) {
    setDetails((prev) => ({ ...prev, [key]: value }));
    if (detailsErrors[key]) setDetailsErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function handleStudyTypeSelect(st: StudyType) {
    setDetails((prev) => ({
      ...prev,
      studyTypeId: st.id,
      studyTypeCode: st.code,
      studyTypeName: st.name,
    }));
    if (detailsErrors.studyTypeCode)
      setDetailsErrors((prev) => ({ ...prev, studyTypeCode: undefined }));
  }

  async function handleSubmit() {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        accountId: account.accountId,
        contactId: account.contactId,
        studyTypeCode: details.studyTypeCode || undefined,
        requiredStudy: details.requiredStudy || undefined,
        serviceCount: details.serviceCount ? Number(details.serviceCount) : undefined,
        reportSubmissionDate: details.reportSubmissionDate || undefined,
        reportValidityDate: details.reportValidityDate || undefined,
        crmCode: details.crmCode || undefined,
        tahseelReferenceNo: details.tahseelReferenceNo || undefined,
      };

      const created = await api.post('study-requests', { json: body }).json<{ id: string }>();
      navigate({ to: '/study-requests/$requestId', params: { requestId: created.id } });
    } catch (err: unknown) {
      const body = err as { json?: () => Promise<{ message?: string }> };
      let msg = 'An unexpected error occurred. Please try again.';
      try {
        const parsed = await body.json?.();
        if (parsed?.message) msg = String(parsed.message);
      } catch {
        if (err instanceof Error) msg = err.message;
      }
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppLayout noPadding>
      <div className="flex flex-col min-h-[calc(100vh-4rem)]">
        {/* Header */}
        <div className="border-b border-gray-200 bg-white px-6 py-5 lg:px-8">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
            <button
              type="button"
              onClick={() => navigate({ to: '/study-requests', search: { page: 1, limit: 20 } })}
              className="hover:text-gray-800 transition-colors"
            >
              Study Requests
            </button>
            <ChevronRight size={12} />
            <span className="text-gray-800 font-medium">New Study Request</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">New Study Request</h1>
          <p className="mt-1 text-sm text-gray-500">
            Register a study request manually and begin the workflow.
          </p>
        </div>

        {/* Body */}
        <div className="flex flex-1">
          {/* Main form */}
          <div className="flex-1 min-w-0 px-6 py-6 lg:px-8">
            <StepIndicator current={step} />

            {/* Step 1 */}
            {step === 1 && (
              <div className="space-y-5">
                <AccountContactStep
                  value={account}
                  onChange={(updates) => setAccount((prev) => ({ ...prev, ...updates }))}
                  errors={{ accountId: undefined, contactId: undefined }}
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    disabled={!accountValid}
                    onClick={() => setStep(2)}
                    className={cn(
                      'flex items-center gap-2 rounded-xl px-5 py-2 text-sm font-semibold transition-all',
                      accountValid
                        ? 'bg-brand-500 text-white hover:bg-brand-600 shadow-sm shadow-brand-500/25'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed',
                    )}
                  >
                    Continue <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="space-y-5">
                {/* Study type selector */}
                <Field label="Study Type" required error={detailsErrors.studyTypeCode}>
                  {studyTypesLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                      <Loader2 size={14} className="animate-spin" /> Loading study types…
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {(studyTypes ?? []).map((st) => {
                        const selected = details.studyTypeCode === st.code;
                        return (
                          <button
                            key={st.code}
                            type="button"
                            onClick={() => handleStudyTypeSelect(st)}
                            className={cn(
                              'rounded-xl border-2 p-4 text-left transition-all',
                              selected
                                ? 'border-brand-500 bg-brand-50 shadow-md shadow-brand-500/10'
                                : 'border-gray-200 bg-white hover:border-brand-200 hover:shadow-sm',
                            )}
                          >
                            {selected && (
                              <div className="mb-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand-500">
                                <Check size={11} className="text-white" />
                              </div>
                            )}
                            <p className="text-sm font-semibold text-gray-900">{st.name}</p>
                            <p className="mt-0.5 text-xs text-gray-400">{st.code}</p>
                            <p className="mt-1 text-xs font-medium text-brand-600">
                              AED{' '}
                              {Number(st.totalFee).toLocaleString('en-AE', {
                                minimumFractionDigits: 2,
                              })}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </Field>

                {/* Fee preview */}
                {details.studyTypeId && <FeePreview studyTypeId={details.studyTypeId} />}

                {/* Other fields */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Required Study">
                    <textarea
                      rows={3}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 resize-none"
                      placeholder="Describe the required study…"
                      value={details.requiredStudy}
                      onChange={(e) => handleDetailsChange('requiredStudy', e.target.value)}
                    />
                  </Field>

                  <Field label="Service Count">
                    <input
                      type="number"
                      min={1}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                      placeholder="e.g. 1"
                      value={details.serviceCount}
                      onChange={(e) => handleDetailsChange('serviceCount', e.target.value)}
                    />
                  </Field>

                  <Field label="Report Submission Date">
                    <input
                      type="date"
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                      value={details.reportSubmissionDate}
                      onChange={(e) => handleDetailsChange('reportSubmissionDate', e.target.value)}
                    />
                  </Field>

                  <Field label="Report Validity Date">
                    <input
                      type="date"
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                      value={details.reportValidityDate}
                      onChange={(e) => handleDetailsChange('reportValidityDate', e.target.value)}
                    />
                  </Field>

                  <Field label="CRM Code">
                    <input
                      type="text"
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                      placeholder="e.g. CRM-12345"
                      value={details.crmCode}
                      onChange={(e) => handleDetailsChange('crmCode', e.target.value)}
                    />
                  </Field>

                  <Field label="Tahseel Reference Number">
                    <input
                      type="text"
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                      placeholder="e.g. 26070805"
                      value={details.tahseelReferenceNo}
                      onChange={(e) => handleDetailsChange('tahseelReferenceNo', e.target.value)}
                    />
                  </Field>
                </div>

                <div className="flex justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (validateDetails()) setStep(3);
                    }}
                    className="flex items-center gap-2 rounded-xl bg-brand-500 px-5 py-2 text-sm font-semibold text-white shadow-sm shadow-brand-500/25 hover:bg-brand-600 transition-all"
                  >
                    Review <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div className="space-y-5">
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                  <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
                    <p className="text-sm font-semibold text-gray-700">Review Summary</p>
                  </div>
                  <div className="divide-y divide-gray-100 text-sm">
                    <ReviewRow label="Account" value={account.accountName} />
                    <ReviewRow label="Contact" value={account.contactName} />
                    <ReviewRow
                      label="Study Type"
                      value={details.studyTypeName || details.studyTypeCode || '—'}
                    />
                    <ReviewRow label="Required Study" value={details.requiredStudy || '—'} />
                    <ReviewRow
                      label="Service Count"
                      value={details.serviceCount ? details.serviceCount : '—'}
                    />
                    <ReviewRow
                      label="Report Submission"
                      value={details.reportSubmissionDate || '—'}
                    />
                    <ReviewRow label="Report Validity" value={details.reportValidityDate || '—'} />
                    <ReviewRow label="CRM Code" value={details.crmCode || '—'} />
                    <ReviewRow
                      label="Tahseel Reference Number"
                      value={details.tahseelReferenceNo || '—'}
                    />
                  </div>
                </div>

                {details.studyTypeId && <FeePreview studyTypeId={details.studyTypeId} />}

                {submitError && (
                  <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-3.5">
                    <AlertCircle size={16} className="shrink-0 text-red-500" />
                    <p className="text-sm text-red-700">{submitError}</p>
                  </div>
                )}

                <div className="flex justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    disabled={submitting}
                    className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className={cn(
                      'flex items-center gap-2 rounded-xl px-5 py-2 text-sm font-semibold transition-all',
                      submitting
                        ? 'bg-brand-300 text-white cursor-not-allowed'
                        : 'bg-brand-500 text-white hover:bg-brand-600 shadow-sm shadow-brand-500/25',
                    )}
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={15} className="animate-spin" /> Submitting…
                      </>
                    ) : (
                      <>
                        <Check size={15} /> Submit
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar summary */}
          <div className="w-72 shrink-0 px-6 py-6 hidden lg:block">
            <div className="sticky top-20">
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="border-b border-gray-100 px-5 py-4">
                  <p className="text-sm font-semibold text-gray-900">Request Summary</p>
                </div>
                <div className="p-5 space-y-3 text-sm">
                  {account.accountName ? (
                    <SummaryRow label="Account" value={account.accountName} />
                  ) : (
                    <p className="text-xs text-gray-400 italic">Fill in steps to see summary</p>
                  )}
                  {account.contactName && (
                    <SummaryRow label="Contact" value={account.contactName} />
                  )}
                  {details.studyTypeName && (
                    <SummaryRow label="Study Type" value={details.studyTypeName} />
                  )}
                  {details.reportSubmissionDate && (
                    <SummaryRow label="Report Due" value={details.reportSubmissionDate} />
                  )}
                </div>
                <div className="border-t border-gray-100 px-5 py-4">
                  <div className="flex gap-2 text-xs">
                    {[1, 2, 3].map((s) => (
                      <div
                        key={s}
                        className={cn(
                          'h-1.5 flex-1 rounded-full transition-colors',
                          s < step ? 'bg-success-400' : s === step ? 'bg-brand-500' : 'bg-gray-200',
                        )}
                      />
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Step {step} of {STEPS.length} —{' '}
                    <span className="font-medium text-gray-700">
                      {STEPS.find((s) => s.id === step)?.label}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 px-5 py-3">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="font-medium text-gray-900 text-right">{value}</span>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-gray-400 shrink-0">{label}</span>
      <span className="font-semibold text-right text-gray-800 truncate max-w-[160px]">{value}</span>
    </div>
  );
}
